import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";

export const Portfolio = () => {
  const { t } = useI18n();
  const projects = [
    {
      title: t("work.example_vitrine_t"),
      category: t("work.example_vitrine_c"),
      to: "/exemples/vitrine",
      key: "vitrine",
      span: "lg:col-span-7",
      aspect: "aspect-[16/10] sm:aspect-[16/9]",
      label: "VITRINE",
      url: "maison-lautrec.fr",
      Mockup: VitrineMockup,
    },
    {
      title: t("work.example_ecom_t"),
      category: t("work.example_ecom_c"),
      to: "/exemples/ecommerce",
      key: "ecommerce",
      span: "lg:col-span-5",
      aspect: "aspect-[16/10] sm:aspect-[16/9]",
      label: "E-COMMERCE",
      url: "atelierbruyere.fr",
      Mockup: EcommerceMockup,
    },
    {
      title: t("work.example_app_t"),
      category: t("work.example_app_c"),
      to: "/exemples/application",
      key: "application",
      span: "lg:col-span-12",
      aspect: "aspect-[16/10] sm:aspect-[21/8]",
      label: "APPLICATION",
      url: "app.pleiade.io",
      Mockup: AppMockup,
    },
  ];
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-24 sm:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-zinc-500 mb-4">
              {t("work.label")}
            </p>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tighter max-w-2xl">
              {t("work.title_a")}
              <em className="italic text-zinc-400">{t("work.title_em")}</em>
              {t("work.title_b")}
            </h2>
          </div>
          <p className="text-sm text-zinc-400 max-w-md">{t("work.intro")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {projects.map(({ Mockup, ...p }, idx) => (
            <Link
              key={p.key}
              to={p.to}
              data-testid={`portfolio-card-${p.key}`}
              className={`group relative block border border-white/10 bg-zinc-900/40 hover:border-white/30 transition-colors overflow-hidden ${p.span}`}
            >
              {/* Browser frame */}
              <div className="px-4 py-2.5 border-b border-white/10 bg-zinc-950 flex items-center gap-3">
                <div className="flex gap-1.5">
                  <span className="block w-2.5 h-2.5 rounded-full bg-zinc-700 group-hover:bg-rose-400/70 transition-colors" />
                  <span className="block w-2.5 h-2.5 rounded-full bg-zinc-700 group-hover:bg-amber-400/70 transition-colors" />
                  <span className="block w-2.5 h-2.5 rounded-full bg-zinc-700 group-hover:bg-emerald-400/70 transition-colors" />
                </div>
                <div className="flex-1 min-w-0 px-3 py-1 bg-zinc-900 border border-white/5 text-[10px] font-mono text-zinc-500 truncate">
                  https://{p.url}
                </div>
                <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 hidden sm:block">
                  {p.label}
                </span>
              </div>

              {/* Mockup */}
              <div className={`relative ${p.aspect} overflow-hidden`}>
                <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-[1.02]">
                  <Mockup />
                </div>
              </div>

              {/* Caption */}
              <div className="px-5 sm:px-6 py-5 border-t border-white/10 bg-zinc-950 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1 truncate">
                    {p.category}
                  </p>
                  <h3 className="font-display text-xl sm:text-2xl tracking-tight truncate">
                    {p.title}
                  </h3>
                </div>
                <span className="flex-shrink-0 inline-flex items-center gap-2 text-xs text-zinc-300 group-hover:text-white">
                  <span className="hidden sm:inline">{t("work.cta_open")}</span>
                  <span className="block w-9 h-9 border border-white/20 group-hover:bg-white group-hover:text-zinc-950 transition-colors flex items-center justify-center">
                    <ArrowUpRight size={14} />
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────────────────────── Mockups ───────────────────────── */

const VitrineMockup = () => (
  <div className="absolute inset-0 bg-stone-50 text-stone-900 overflow-hidden flex flex-col">
    {/* Header */}
    <div className="px-5 py-2.5 flex items-center justify-between border-b border-stone-200 flex-shrink-0">
      <p className="font-display italic text-base">Maison Lautrec</p>
      <div className="hidden sm:flex gap-5">
        <span className="text-[9px] uppercase tracking-[0.2em] text-stone-600">Projets</span>
        <span className="text-[9px] uppercase tracking-[0.2em] text-stone-600">Approche</span>
        <span className="text-[9px] uppercase tracking-[0.2em] text-stone-600">Contact</span>
      </div>
      <span className="text-[9px] uppercase tracking-[0.2em] text-stone-500 hidden md:inline">
        Bordeaux · 2024
      </span>
    </div>
    {/* Hero */}
    <div className="px-5 pt-4 pb-3 flex-1 min-h-0">
      <p className="text-[8px] uppercase tracking-[0.3em] text-stone-500 mb-1.5">— Architecture intérieure</p>
      <p className="font-display text-xl sm:text-2xl md:text-3xl leading-[1.05] tracking-tighter">
        L'art de l'<em className="italic text-stone-600">espace</em>,
        <br />
        du sol au plafond.
      </p>
      <div className="mt-3 flex gap-2">
        <span className="bg-stone-900 text-stone-50 px-2.5 py-1 text-[8px] uppercase tracking-[0.2em]">
          Voir nos projets
        </span>
        <span className="border border-stone-900 px-2.5 py-1 text-[8px] uppercase tracking-[0.2em]">
          Prendre rendez-vous
        </span>
      </div>
    </div>
    {/* Project tiles */}
    <div className="px-5 pb-3 grid grid-cols-3 gap-1.5 flex-shrink-0">
      {[
        { c: "bg-amber-100", n: "Haussmann", l: "Paris 7e" },
        { c: "bg-rose-100", n: "Villa Med.", l: "Biarritz" },
        { c: "bg-emerald-100", n: "Loft Indus.", l: "Bordeaux" },
      ].map((p, i) => (
        <div key={i} className="aspect-[5/4] relative overflow-hidden">
          <div className={`absolute inset-0 ${p.c}`} />
          <div className="absolute bottom-1 left-1.5 right-1.5">
            <p className="text-[6px] uppercase tracking-[0.2em] text-stone-700">{p.l}</p>
            <p className="font-display text-[10px] text-stone-900 truncate">{p.n}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EcommerceMockup = () => (
  <div className="absolute inset-0 bg-amber-50 text-stone-900 overflow-hidden flex flex-col">
    {/* Header */}
    <div className="px-4 py-2 flex items-center justify-between border-b border-stone-300/60 flex-shrink-0">
      <p className="font-display italic text-sm">Atelier Bruyère</p>
      <div className="hidden sm:flex gap-3 text-[8px] uppercase tracking-[0.2em] text-stone-700">
        <span>Boutique</span>
        <span>Histoire</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="block w-3 h-3 border border-stone-700" />
        <span className="block w-4 h-4 border border-stone-700 relative">
          <span className="absolute -top-1 -right-1 bg-stone-900 text-amber-50 text-[6px] font-mono w-2.5 h-2.5 flex items-center justify-center leading-none">
            2
          </span>
        </span>
      </div>
    </div>
    {/* Title bar */}
    <div className="px-4 py-2 flex items-center justify-between border-b border-stone-300/40 flex-shrink-0">
      <div>
        <p className="text-[7px] uppercase tracking-[0.25em] text-stone-500">— Printemps 2026</p>
        <p className="font-display text-sm italic">Mode artisanale</p>
      </div>
      <span className="hidden sm:block text-[8px] uppercase tracking-[0.2em] text-stone-500 border border-stone-400 px-2 py-0.5">
        6 pièces
      </span>
    </div>
    {/* Product grid 3x2 */}
    <div className="grid grid-cols-3 grid-rows-2 flex-1 min-h-0 bg-stone-300/30 gap-px">
      {[
        { c: "bg-rose-100", n: "Cabas Élise", p: "290€" },
        { c: "bg-amber-100", n: "Sandales", p: "180€" },
        { c: "bg-orange-100", n: "Foulard", p: "95€" },
        { c: "bg-stone-200", n: "Veste", p: "420€" },
        { c: "bg-emerald-100", n: "Robe", p: "340€" },
        { c: "bg-yellow-100", n: "Ceinture", p: "75€" },
      ].map((it, i) => (
        <div key={i} className="bg-amber-50 flex flex-col">
          <div className={`flex-1 ${it.c} relative`}>
            <span className="absolute top-1 right-1 block w-3 h-3 bg-amber-50/70 backdrop-blur-sm flex items-center justify-center">
              <span className="block w-1 h-1 bg-stone-900" />
            </span>
          </div>
          <div className="px-1.5 py-1 flex items-center justify-between text-[8px] flex-shrink-0">
            <span className="font-display truncate">{it.n}</span>
            <span className="font-mono text-stone-700">{it.p}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AppMockup = () => (
  <div className="absolute inset-0 bg-zinc-950 text-zinc-100 overflow-hidden flex">
    {/* Sidebar */}
    <div className="w-32 sm:w-44 border-r border-white/10 p-3 hidden sm:flex flex-col flex-shrink-0">
      <p className="font-display italic text-sm mb-1">Pléiade</p>
      <p className="text-[8px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Workspace · Acme</p>
      <div className="space-y-1">
        <div className="bg-white text-zinc-950 px-2 py-1.5 text-[9px]">Dashboard</div>
        <div className="px-2 py-1.5 text-[9px] text-zinc-400">Tâches</div>
        <div className="px-2 py-1.5 text-[9px] text-zinc-400">Projets</div>
        <div className="px-2 py-1.5 text-[9px] text-zinc-400">Équipe</div>
        <div className="px-2 py-1.5 text-[9px] text-zinc-400">Calendrier</div>
      </div>
      <div className="mt-auto pt-3 border-t border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-white text-zinc-950 flex items-center justify-center font-display text-[8px]">
            AC
          </div>
          <p className="text-[8px] text-zinc-400">Anaïs C.</p>
        </div>
      </div>
    </div>
    {/* Main */}
    <div className="flex-1 p-3 sm:p-4 min-w-0 flex flex-col gap-2.5">
      <div>
        <p className="text-[8px] uppercase tracking-[0.25em] text-zinc-500">— Bonjour Anaïs</p>
        <p className="font-display text-sm sm:text-base leading-tight tracking-tighter mt-1">
          3 tâches à finaliser <em className="italic text-emerald-400">aujourd'hui</em>.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-px bg-white/10 border border-white/10 flex-shrink-0">
        {[
          { v: "12", l: "Tâches", t: "+3" },
          { v: "5", l: "Projets", t: "+1" },
          { v: "8", l: "Équipe", t: "—" },
          { v: "84%", l: "Productivité", t: "+12%" },
        ].map((s, i) => (
          <div key={i} className="bg-zinc-950 px-2 py-2">
            <div className="flex items-baseline justify-between">
              <p className="font-display text-base sm:text-lg tracking-tighter">{s.v}</p>
              <span className="font-mono text-[7px] text-emerald-400">{s.t}</span>
            </div>
            <p className="text-[7px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Two columns - tasks + activity */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1 min-h-0">
        <div className="sm:col-span-2 border border-white/10 p-2.5 flex flex-col">
          <p className="text-[7px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex-shrink-0">
            Tâches du jour
          </p>
          <div className="space-y-1.5 flex-1 overflow-hidden">
            {[
              { d: true, t: "Validation wireframes — Acme Corp", p: "Acme · Site web" },
              { d: true, t: "Brief client homepage", p: "Acme · Site web" },
              { d: false, t: "Maquette mobile — Page produit", p: "Lumina Shop" },
              { d: false, t: "Réunion équipe design — Mardi 14h", p: "Interne" },
              { d: false, t: "Recettage v2.4 — Stripe", p: "Lumina Shop" },
            ].map((task, i) => (
              <div key={i} className="flex items-start gap-2 text-[9px]">
                <span
                  className={`block w-2.5 h-2.5 rounded-full border flex-shrink-0 mt-0.5 ${
                    task.d ? "bg-emerald-400 border-emerald-400" : "border-zinc-600"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className={`truncate ${task.d ? "text-zinc-500 line-through" : "text-zinc-200"}`}>
                    {task.t}
                  </p>
                  <p className="text-[7px] uppercase tracking-[0.15em] text-zinc-600 mt-0.5 truncate">
                    {task.p}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 p-2.5 flex flex-col">
          <p className="text-[7px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex-shrink-0">
            Activité
          </p>
          <svg viewBox="0 0 120 40" className="w-full flex-shrink-0" preserveAspectRatio="none">
            <defs>
              <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#10b981" stopOpacity="0.5" />
                <stop offset="1" stopColor="#10b981" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polyline
              fill="none"
              stroke="#fafafa"
              strokeWidth="1"
              points="0,32 20,22 40,26 60,14 80,18 100,8 120,16"
            />
            <polygon
              fill="url(#g)"
              points="0,32 20,22 40,26 60,14 80,18 100,8 120,16 120,40 0,40"
            />
          </svg>
          <div className="mt-2 space-y-1.5 flex-1 overflow-hidden">
            {[
              { who: "Marc P.", what: "a clos une tâche", when: "12 min" },
              { who: "Léa R.", what: "a commenté Lumina Shop", when: "1 h" },
              { who: "Vous", what: "avez créé Refonte 2026", when: "3 h" },
            ].map((a, i) => (
              <div key={i} className="border-l border-white/10 pl-2">
                <p className="text-[8px] text-zinc-300 truncate">
                  <span className="font-medium">{a.who}</span> {a.what}
                </p>
                <p className="text-[7px] text-zinc-600 mt-0.5 font-mono">{a.when}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);
